if (!customElements.get('machine-finder')) {
  class MachineFinder extends HTMLElement {
    connectedCallback() {
      this.COUPLERS = [
        { size: 'S30', min: 0,    max: 2.7      },
        { size: 'S40', min: 2.7,  max: 6        },
        { size: 'S45/S50', sizes: ['S45', 'S50'], min: 6, max: 9 },
        { size: 'S60', min: 9,    max: 14       },
        { size: 'S70', min: 14,   max: 20       },
        { size: 'S80', min: 20,   max: Infinity },
      ];
      this.MAX_T  = 35;
      this.MAX_KG = 35000;
      this.unit   = 't';

      // Sidebar mode: filter products in the current page grid
      this.isSidebar = this.classList.contains('machine-finder--sidebar');

      // Tabs & panels
      this.tabs   = this.querySelectorAll('.js-mf-tab');
      this.panels = this.querySelectorAll('.js-mf-panel');

      // Excavator controls
      this.weightNum   = this.querySelector('.js-mf-weight-num');
      this.weightRange = this.querySelector('.js-mf-weight-range');
      this.unitBtns    = this.querySelectorAll('.js-mf-unit');
      this.resultRow   = this.querySelector('.js-mf-result');
      this.detectedEl  = this.querySelector('.js-mf-detected');
      this.overrideSel = this.querySelector('.js-mf-override');
      this.searchExc   = this.querySelector('.js-mf-search-exc');

      // Wheelloader / Tractor controls
      this.hitchWL  = this.querySelector('.js-mf-hitch-wl');
      this.searchWL = this.querySelector('.js-mf-search-wl');
      this.hitchTR  = this.querySelector('.js-mf-hitch-tr');
      this.searchTR = this.querySelector('.js-mf-search-tr');

      this._bind();
      this._restoreFromUrl();
    }

    _bind() {
      // Tab switching — reset filter when tab changes
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          this.tabs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
          this.panels.forEach(p => p.classList.remove('is-active'));
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');
          const panel = this.querySelector(`[data-panel="${tab.dataset.tab}"]`);
          if (panel) panel.classList.add('is-active');
          // Clear any active filter when tab changes
          if (this.isSidebar) this._showAll();
        });
      });

      // Unit toggle
      this.unitBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const next = btn.dataset.unit;
          if (next === this.unit) return;
          let val = parseFloat(this.weightNum.value) || 0;
          if (next === 'kg') {
            val = +(val * 1000).toFixed(0);
            this.weightNum.setAttribute('max', this.MAX_KG);
            this.weightNum.setAttribute('step', '1');
            this.weightRange.setAttribute('max', this.MAX_KG);
            this.weightRange.setAttribute('step', '10');
          } else {
            val = +(val / 1000).toFixed(2);
            this.weightNum.setAttribute('max', this.MAX_T);
            this.weightNum.setAttribute('step', '0.1');
            this.weightRange.setAttribute('max', this.MAX_T);
            this.weightRange.setAttribute('step', '0.1');
          }
          this.unit = next;
          this.weightNum.value  = val;
          this.weightRange.value = val;
          this.unitBtns.forEach(b => b.classList.toggle('is-active', b === btn));
          this._updateRange(this.weightRange);
          this._updateExc();
        });
      });

      // Excavator weight inputs
      this.weightNum.addEventListener('input', () => {
        this.weightRange.value = this.weightNum.value;
        this._updateRange(this.weightRange);
        this._updateExc();
      });
      this.weightRange.addEventListener('input', () => {
        this.weightNum.value = this.weightRange.value;
        this._updateRange(this.weightRange);
        this._updateExc();
      });

      // Manual coupler override
      this.overrideSel.addEventListener('change', () => this._updateExc());

      // Wheelloader & tractor selects
      if (this.hitchWL) this.hitchWL.addEventListener('change', () => this._updateHitch(this.hitchWL, this.searchWL));
      if (this.hitchTR) this.hitchTR.addEventListener('change', () => this._updateHitch(this.hitchTR, this.searchTR));

      // Sidebar: explicit navigation to prevent any drawer/facet interception
      if (this.isSidebar) {
        [this.searchExc, this.searchWL, this.searchTR].filter(Boolean).forEach(btn => {
          btn.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();
            const url = btn.dataset.searchUrl;
            if (url) window.location.href = url;
          });
        });
      }
    }

    // ── Restore state from URL params ─────────────────────────
    _restoreFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('mf_tab');
      if (!tab) return;

      // Activate the correct tab
      const targetTab = this.querySelector(`.js-mf-tab[data-tab="${tab}"]`);
      if (targetTab) {
        this.tabs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
        this.panels.forEach(p => p.classList.remove('is-active'));
        targetTab.classList.add('is-active');
        targetTab.setAttribute('aria-selected', 'true');
        const panel = this.querySelector(`[data-panel="${tab}"]`);
        if (panel) panel.classList.add('is-active');
      }

      if (tab === 'excavator') {
        const weightT  = parseFloat(params.get('mf_weight') || '0');
        const unit     = params.get('mf_unit') || 't';
        const override = params.get('mf_override') || '';

        // Restore unit
        if (unit !== this.unit) {
          this.unit = unit;
          this.unitBtns.forEach(b => b.classList.toggle('is-active', b.dataset.unit === unit));
          if (unit === 'kg') {
            this.weightNum.setAttribute('max', this.MAX_KG);
            this.weightNum.setAttribute('step', '1');
            this.weightRange.setAttribute('max', this.MAX_KG);
            this.weightRange.setAttribute('step', '10');
          }
        }

        // Restore weight (weightT is always stored in tonnes)
        if (weightT > 0) {
          const display = unit === 'kg' ? +(weightT * 1000).toFixed(0) : weightT;
          this.weightNum.value   = display;
          this.weightRange.value = display;
          this._updateRange(this.weightRange);
        }

        // Restore manual override
        if (override) this.overrideSel.value = override;

        this._updateExc();

      } else if (tab === 'wheelloader') {
        const q = params.get('q') || '';
        if (q && this.hitchWL) {
          this.hitchWL.value = q;
          this._updateHitch(this.hitchWL, this.searchWL);
        }

      } else if (tab === 'tractor') {
        const q = params.get('q') || '';
        if (q && this.hitchTR) {
          this.hitchTR.value = q;
          this._updateHitch(this.hitchTR, this.searchTR);
        }
      }
    }

    // ── Slider fill track ─────────────────────────────────────
    _updateRange(rangeEl) {
      const min = parseFloat(rangeEl.min) || 0;
      const max = parseFloat(rangeEl.max) || this.MAX_T;
      const val = parseFloat(rangeEl.value) || 0;
      const pct = ((val - min) / (max - min)) * 100;
      rangeEl.style.setProperty('--range-fill', pct + '%');
    }

    // ── Coupler detection ─────────────────────────────────────
    _detect(weightT) {
      if (weightT <= 0) return null;
      return this.COUPLERS.find(c => weightT >= c.min && weightT < c.max) || null;
    }

    // ── Excavator update ──────────────────────────────────────
    _updateExc() {
      const raw     = parseFloat(this.weightNum.value) || 0;
      const weightT = this.unit === 'kg' ? raw / 1000 : raw;
      const auto    = this._detect(weightT);

      if (auto && !this.overrideSel.value) {
        this.resultRow.removeAttribute('hidden');
        this.detectedEl.textContent = auto.size;
      } else {
        this.resultRow.setAttribute('hidden', '');
      }

      const override = this.overrideSel.value;
      // When auto-detected and the coupler covers multiple sizes (e.g. S45/S50), search all of them
      const searchQuery = override
        ? override
        : auto
          ? (auto.sizes ? auto.sizes.join(' OR ') : auto.size)
          : null;

      if (searchQuery) {
        // Include machine finder state in URL so it can be restored on the results page
        let url = `/search?q=${encodeURIComponent(searchQuery)}&type=product&mf_tab=excavator&mf_weight=${weightT}&mf_unit=${this.unit}`;
        if (override) url += `&mf_override=${encodeURIComponent(override)}`;

        this.searchExc.href = url;
        this.searchExc.dataset.searchUrl = url;
        this.searchExc.removeAttribute('aria-disabled');
      } else {
        this.searchExc.href = '/search?type=product';
        delete this.searchExc.dataset.searchUrl;
        this.searchExc.setAttribute('aria-disabled', 'true');
      }

      // Sidebar: also filter products on this page
      if (this.isSidebar) {
        this._filterByQuery(size, this.querySelector('.js-mf-filter-hint'));
      }
    }

    // ── Hitch update (wheelloader / tractor) ──────────────────
    _updateHitch(selectEl, searchBtn) {
      const val   = selectEl.value;
      const tabId = selectEl === this.hitchWL ? 'wheelloader' : 'tractor';

      if (val) {
        // Include tab in URL so the results page can restore the correct tab + selection
        const url = `/search?q=${encodeURIComponent(val)}&type=product&mf_tab=${tabId}`;
        searchBtn.href = url;
        searchBtn.dataset.searchUrl = url;
        searchBtn.removeAttribute('aria-disabled');
      } else {
        searchBtn.href = '/search?type=product';
        delete searchBtn.dataset.searchUrl;
        searchBtn.setAttribute('aria-disabled', 'true');
      }

      // Sidebar: filter by hitch tag
      if (this.isSidebar) {
        const panel = selectEl.closest('.js-mf-panel');
        const hint  = panel ? panel.querySelector('.js-mf-filter-hint') : null;
        this._filterByQuery(val, hint);
      }
    }

    // ── Core page filter ──────────────────────────────────────
    _getItems() {
      // Query fresh every time so late-rendered grids are included
      return Array.from(document.querySelectorAll('.js-pagination-result'));
    }

    _filterByQuery(query, hintEl) {
      const items = this._getItems();
      if (!items.length) return;

      if (!query) {
        this._showAll();
        if (hintEl) hintEl.textContent = '';
        return;
      }

      const q = query.toLowerCase().trim();
      let visible = 0;

      items.forEach(li => {
        const card = li.querySelector('product-card');
        if (!card) { li.style.display = ''; visible++; return; }

        const title = (card.dataset.title || '').toLowerCase();
        const tags  = (card.dataset.tags  || '').toLowerCase();

        const match = title.includes(q) ||
                      tags.split(',').some(t => t.trim().includes(q));

        li.style.display = match ? '' : 'none';
        if (match) visible++;
      });

      if (hintEl) {
        hintEl.textContent = visible > 0
          ? 'Viser ' + visible + ' produkt' + (visible !== 1 ? 'er' : '')
          : 'Ingen produkter funnet for ' + query;
      }
    }

    _showAll() {
      this._getItems().forEach(li => { li.style.display = ''; });
    }
  }

  customElements.define('machine-finder', MachineFinder);
}
